(() => {
  'use strict';

  const ELEMENT_NAME = 's-calorie-calculator';

  if (customElements.get(ELEMENT_NAME)) {
    return;
  }

  class CalorieCalculator extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') {
        return;
      }

      this.dataset.initialized = 'true';
      this.classList.remove('aav-no-js');

      const root = this;
      const $ = (id) => root.querySelector(`#${id}`);

      /* ---------------- Veterinary model ----------------
         RER (resting energy) = 70 * kg^0.75  for companion mammals.
         Daily energy (DER/MER) = RER * life-stage factor.
         Birds: BMR = k * kg^0.75 (k=78 non-passerine, 129 passerine) * activity factor.
         Horse: NRC 2007 digestible energy, kcal/day = coeff * kg bodyweight.
         Ruminants: reported as dry-matter intake (% bodyweight) plus a maintenance
         ME estimate, because production stage drives requirement and rations must be
         balanced properly. Pig: monogastric ME estimate in kcal.
      -----------------------------------------------------*/

      var SPECIES = {
        dog:{ic:'🐕',nm:'Dog',model:'rer',unit:'kg',
          stages:[
            {t:'Adult, neutered (typical pet)',f:1.6},
            {t:'Adult, entire / not neutered',f:1.8},
            {t:'Adult, low activity or prone to weight gain',f:1.4},
            {t:'Adult, active with lots of exercise',f:2.0},
            {t:'Puppy, up to 4 months',f:3.0},
            {t:'Puppy, 4 months to adult size',f:2.0},
            {t:'Senior, less active',f:1.4},
            {t:'Working dog, moderate work',f:3.0},
            {t:'Working dog, heavy work',f:4.5},
            {t:'Weight loss program',f:1.0,target:true},
            {t:'Weight gain / underweight',f:1.7,target:true}
          ]},
        cat:{ic:'🐈',nm:'Cat',model:'rer',unit:'kg',
          stages:[
            {t:'Adult, neutered (typical pet)',f:1.2},
            {t:'Adult, entire / not neutered',f:1.4},
            {t:'Adult, indoor and low activity',f:1.0},
            {t:'Adult, active',f:1.4},
            {t:'Kitten, up to 4 months',f:2.5},
            {t:'Kitten, 4 months to 1 year',f:2.0},
            {t:'Senior',f:1.1},
            {t:'Weight loss program',f:0.8,target:true},
            {t:'Weight gain / underweight',f:1.4,target:true}
          ]},
        ferret:{ic:'🦦',nm:'Ferret',model:'rer',unit:'g',approx:true,
          stages:[
            {t:'Adult maintenance',f:2.5},
            {t:'Young, growing or breeding',f:3.5}
          ]},
        rabbit:{ic:'🐇',nm:'Rabbit',model:'rer',unit:'kg',herb:true,
          stages:[
            {t:'Adult maintenance',f:1.6},
            {t:'Young / growing',f:2.0},
            {t:'Pregnant or nursing',f:3.0},
            {t:'Overweight, needs to slim down',f:1.2}
          ]},
        guinea:{ic:'🐹',nm:'Guinea pig',model:'rer',unit:'g',herb:true,
          stages:[
            {t:'Adult maintenance',f:1.6},
            {t:'Young / growing',f:2.0},
            {t:'Pregnant or nursing',f:3.0}
          ]},
        bird:{ic:'🦜',nm:'Bird',model:'bird',unit:'g',approx:true,
          stages:[
            {t:'Pet maintenance',f:1.5},
            {t:'Active, breeding, moulting or growing',f:2.5}
          ]},
        horse:{ic:'🐎',nm:'Horse',model:'horse',unit:'kg',
          stages:[
            {t:'Maintenance, average keeper',c:33.3},
            {t:'Maintenance, easy keeper (good doer)',c:30.3},
            {t:'Maintenance, hard keeper',c:36.3},
            {t:'Light work (1 to 3 hrs/week)',c:40.0},
            {t:'Moderate work',c:46.6},
            {t:'Heavy work',c:53.3},
            {t:'Very heavy work / racing',c:63.3}
          ]},
        cattle:{ic:'🐄',nm:'Cattle',model:'rumin',unit:'kg',dmi:{maint:2.0,grow:2.5,lact:3.5},mecoef:0.13},
        sheep:{ic:'🐑',nm:'Sheep',model:'rumin',unit:'kg',dmi:{maint:2.5,grow:3.0,lact:4.0},mecoef:0.10},
        goat:{ic:'🐐',nm:'Goat',model:'rumin',unit:'kg',dmi:{maint:3.0,grow:3.5,lact:4.5},mecoef:0.10},
        pig:{ic:'🐖',nm:'Pig',model:'pig',unit:'kg',
          stages:[
            {t:'Adult maintenance',f:1.0},
            {t:'Growing / finishing',f:2.6},
            {t:'Pregnant sow',f:1.4},
            {t:'Lactating sow',f:3.5}
          ]}
      };

      var state={species:'dog',unit:'kg',foodMode:'preset',mode:'owner'};
      var WEIGHT_LIMITS={
        dog:{min:0.05,max:150},cat:{min:0.05,max:30},ferret:{min:0.05,max:5},rabbit:{min:0.1,max:25},
        guinea:{min:0.05,max:5},bird:{min:0.005,max:200},horse:{min:20,max:1500},cattle:{min:10,max:2000},
        sheep:{min:1,max:250},goat:{min:1,max:250},pig:{min:0.2,max:1000}
      };

      /* Food-type presets: typical as-fed energy density in kcal per 100 g.
         These are category averages for convenience. The exact figure on the
         owner's bag is always more accurate, so we nudge toward label entry.
         dry:true means a dry food where a rough cup guide is meaningful. */
      var FOODGROUP={dog:'dog',cat:'cat',ferret:'ferret',rabbit:'herb',guinea:'herb',bird:'bird',horse:'horse',pig:'pig'};
      var PRESETS={
        dog:[
          {t:'Dry food, adult maintenance',k:375,dry:true},
          {t:'Dry food, puppy / active / working',k:425,dry:true},
          {t:'Dry food, light / weight control / senior',k:320,dry:true},
          {t:'Wet / canned food',k:95},
          {t:'Semi-moist / food roll',k:260},
          {t:'Air-dried or freeze-dried',k:450},
          {t:'Fresh / gently cooked',k:150},
          {t:'Raw (BARF)',k:160}
        ],
        cat:[
          {t:'Dry food, adult',k:390,dry:true},
          {t:'Dry food, kitten / active',k:440,dry:true},
          {t:'Dry food, light / indoor / weight',k:330,dry:true},
          {t:'Wet food, pate',k:90},
          {t:'Wet food, chunks in gravy',k:75},
          {t:'Freeze-dried or air-dried',k:450},
          {t:'Fresh / raw',k:130}
        ],
        ferret:[
          {t:'Ferret or kitten kibble',k:420,dry:true},
          {t:'Raw / whole prey',k:150}
        ],
        herb:[
          {t:'Grass hay pellets',k:250,dry:true},
          {t:'Fresh greens and vegetables',k:20},
          {t:'Muesli mix (not recommended)',k:330,dry:true}
        ],
        bird:[
          {t:'Formulated pellets',k:350,dry:true},
          {t:'Seed mix',k:380,dry:true},
          {t:'Fresh vegetables and fruit',k:40}
        ],
        horse:[
          {t:'Grass hay',k:200},
          {t:'Lucerne / alfalfa hay',k:230},
          {t:'Hard feed / concentrate',k:320,dry:true}
        ],
        pig:[
          {t:'Grower / finisher pellets',k:330,dry:true},
          {t:'Sow ration',k:320,dry:true}
        ]
      };

      // build species tiles
      var grid=$('speciesGrid');
      Object.keys(SPECIES).forEach(function(key){
        var s=SPECIES[key];
        var d=document.createElement('button');
        d.type='button';d.className='sp'+(key==='dog'?' on':'');d.dataset.k=key;
        d.setAttribute('aria-pressed',key==='dog'?'true':'false');
        d.setAttribute('aria-label',s.nm);
        d.innerHTML='<span class="ic">'+s.ic+'</span><span class="nm">'+s.nm+'</span>';
        d.addEventListener('click', function(){ selectSpecies(key); });
        grid.appendChild(d);
      });

      function setMode(m){
        if(state.mode===m)return;
        state.mode=m;
        $('modeOwner').classList.toggle('on',m==='owner');
        $('modeOwner').setAttribute('aria-pressed',m==='owner'?'true':'false');
        $('modeVet').classList.toggle('on',m==='vet');
        $('modeVet').setAttribute('aria-pressed',m==='vet'?'true':'false');
        root.classList.toggle('vet-mode', m === 'vet');
        if(m==='owner'){ $('rerK').value='70'; }
        updateRerKVisibility();
        // mode changes the maths, so clear any displayed result
        state.der=null;
        $('result').style.display='none';
        $('err').style.display='none';
      }
      function updateRerKVisibility(){
        var show = state.mode==='vet' && SPECIES[state.species].model==='rer';
        $('rerKField').classList.toggle('hide',!show);
      }
      function selectSpecies(key){
        var speciesChanged=state.species!==key;
        if(speciesChanged){
          $('weight').value='';
          $('target').value='';
          state.der=null;
        }
        state.species=key;
        grid.querySelectorAll('.sp').forEach(function(e){var active=e.dataset.k===key;e.classList.toggle('on',active);e.setAttribute('aria-pressed',active?'true':'false')});
        var s=SPECIES[key];
        // default weight unit
        setUnit(s.unit,true);
        // build stage dropdown
        var sel=$('stage');
        var stageField=$('stageField');
        var rumin = s.model==='rumin';
        if(rumin){
          sel.innerHTML='<option value="maint">Maintenance (dry / not producing)</option>'
            +'<option value="grow">Growing</option>'
            +'<option value="lact">Lactating / heavily producing</option>';
          $('stageHint').textContent='Ruminant needs are driven by production stage. This gives a practical feeding estimate, not a balanced ration.';
        } else {
          sel.innerHTML=s.stages.map(function(st,i){return '<option value="'+i+'">'+st.t+'</option>'}).join('');
          $('stageHint').textContent='';
        }
        // bird type
        $('birdTypeField').classList.toggle('hide',key!=='bird');
        updateRerKVisibility();
        // target weight visibility handled on stage change
        sel.onchange=onStageChange; onStageChange();
        buildPresets();
        $('result').style.display='none';
        $('err').style.display='none';
        $('portionErr').style.display='none';
        $('portionCard').classList.remove('has-portions');
      }

      function buildPresets(){
        var arr=PRESETS[FOODGROUP[state.species]]||[];
        var sel=$('foodPreset');
        sel.innerHTML=arr.map(function(p,i){return '<option value="'+i+'">'+p.t+' (about '+p.k+' kcal/100 g)</option>';}).join('');
        sel.onchange=updatePresetHint;
        updatePresetHint();
      }
      function updatePresetHint(){
        var arr=PRESETS[FOODGROUP[state.species]]||[];
        var p=arr[+$('foodPreset').value];
        $('presetHint').textContent = p ? 'Typical value for this food type. For the most accurate result, switch to "Enter exact from bag" and use the number on your label.' : '';
      }
      function setFoodMode(m){
        state.foodMode=m;
        var preset=m==='preset';
        $('mPreset').classList.toggle('on',preset);
        $('mPreset').setAttribute('aria-pressed',preset?'true':'false');
        $('mLabel').classList.toggle('on',!preset);
        $('mLabel').setAttribute('aria-pressed',preset?'false':'true');
        $('presetMode').classList.toggle('hide',m!=='preset');
        $('labelMode').classList.toggle('hide',m!=='label');
      }
      function onFoodUnitChange(){
        var amt=$('foodUnit').value==='peramount';
        $('amountFields').classList.toggle('hide',!amt);
        $('densityFields').classList.toggle('hide',amt);
      }

      function onStageChange(){
        var s=SPECIES[state.species];
        var showTarget=false;
        if(s.stages){
          var st=s.stages[+$('stage').value];
          showTarget = st && st.target;
        }
        $('targetField').classList.toggle('hide',!showTarget);
        $('tgtUnit').textContent=state.unit;
      }

      function setUnit(u,silent){
        var oldUnit=state.unit;
        if(!silent&&oldUnit!==u){
          ['weight','target'].forEach(function(id){
            var field=root.querySelector(`#${id}`);
            var value=parseFloat(field.value);
            if(value>0&&isFinite(value)){
              var kg=oldUnit==='lb'?value*0.453592:oldUnit==='g'?value/1000:value;
              var converted=u==='lb'?kg/0.453592:u==='g'?kg*1000:kg;
              field.value=String(+(converted>=100?converted.toFixed(2):converted.toFixed(4)));
            }
          });
        }
        state.unit=u;
        ['kg','lb','g'].forEach(function(unit){
          var button=root.querySelector('#u'+(unit==='kg'?'Kg':unit==='lb'?'Lb':'G'));
          var active=u===unit;
          button.classList.toggle('on',active);
          button.setAttribute('aria-pressed',active?'true':'false');
        });
        $('tgtUnit').textContent=u;
        var h=$('weightHint');
        if(u==='g')h.textContent='Small animals and birds: enter weight in grams.';
        else h.textContent="Use your animal's actual weight today.";
      }

      function toKg(v){ if(state.unit==='lb')return v*0.453592; if(state.unit==='g')return v/1000; return v; }

      function fmt(n){ return n>=100?Math.round(n).toLocaleString():(n>=10?n.toFixed(0):n.toFixed(1)); }

      function calc(){
        var err=$('err');
        var w=parseFloat($('weight').value);
        if(!(w>0)||!isFinite(w)){ err.textContent='Please enter a valid body weight.'; err.style.display='block'; $('weight').focus(); return; }
        err.style.display='none';
        var s=SPECIES[state.species];
        var kg=toKg(w);
        var limits=WEIGHT_LIMITS[state.species];
        if(limits&&(kg<limits.min||kg>limits.max)){
          err.textContent='Check the weight and selected unit. The supported range for '+s.nm.toLowerCase()+' calculations is '+limits.min+' to '+limits.max+' kg.';
          err.style.display='block';$('weight').focus();return;
        }
        var res=$('result');
        var statline=$('statline');
        var noteBox=$('noteBox');
        var feedNote=$('feedNote');
        var heroLab=$('heroLab');
        var heroVal=$('heroVal');
        var heroRng=$('heroRng');
        statline.innerHTML='';noteBox.style.display='none';noteBox.innerHTML='';
        $('portionCard').style.display='';
        $('portionCard').classList.remove('has-portions');
        $('portionOut').style.display='none';

        if(s.model==='rer'){
          var stage=s.stages[+$('stage').value];
          var wForCalc=kg;
          if(stage.target){
            var tv=parseFloat($('target').value);
            if(!(tv>0)||!isFinite(tv)){ err.textContent='Enter a valid target weight for the weight management plan.'; err.style.display='block'; $('target').focus(); return; }
            wForCalc=toKg(tv);
            if(limits&&(wForCalc<limits.min||wForCalc>limits.max)){err.textContent='Check the target weight and selected unit. Enter a target between '+limits.min+' and '+limits.max+' kg for this species.';err.style.display='block';$('target').focus();return;}
          }
          var K=70;
          if(state.mode==='vet'){
            K=parseFloat($('rerK').value);
            if(!(K>0)||!isFinite(K)||K<30||K>140){
              err.textContent='Enter an RER coefficient between 30 and 140 (standard is 70).';
              err.style.display='block'; $('rerK').focus(); return;
            }
          }
          var rer=K*Math.pow(wForCalc,0.75);
          var der=rer*stage.f;
          state.der=der;
          heroLab.textContent='Daily energy target';
          heroVal.innerHTML=fmt(der)+' <small>kcal/day</small>';
          heroRng.textContent='Typical range '+fmt(der*0.9)+' to '+fmt(der*1.1)+' kcal. Adjust to keep body condition ideal.';
          statline.innerHTML=
            stat('Resting energy (RER)',fmt(rer)+' <small>kcal</small>')+
            (state.mode==='vet'?stat('RER coefficient','K = '+K+(K===70?' (standard)':'')):'')+
            stat('Energy factor','x '+stage.f)+
            stat(stage.target?'Based on target weight':'Based on weight',fmt(wForCalc)+' <small>kg</small>');
          if(state.mode==='vet'&&K!==70){
            noteBox.style.display='block';
            noteBox.innerHTML='<strong>Non-standard coefficient:</strong> this result uses RER = '+K+' &times; kg<sup>0.75</sup> instead of the standard 70. Document the protocol you are following in the patient record.';
          }
          if(s.approx){ noteBox.style.display='block'; noteBox.innerHTML+=(noteBox.innerHTML?'<br><br>':'')+'<strong>Estimate:</strong> ferret energy needs vary widely. Most healthy ferrets do best free feeding a high protein, high fat diet. Use this as a guide and watch body condition.'; }
          feedNote.innerHTML=feedText(state.species);
          feedNote.style.display='block';
        }
        else if(s.model==='bird'){
          var bt=$('birdType').value;
          var k=bt==='p'?129:78;
          var stageB=s.stages[+$('stage').value];
          var bmr=k*Math.pow(kg,0.75);
          var derB=bmr*stageB.f;
          state.der=derB;
          heroLab.textContent='Daily energy target';
          heroVal.innerHTML=fmt(derB)+' <small>kcal/day</small>';
          heroRng.textContent='Bird energy varies with species and temperature. Weigh regularly and keep the keel muscle well covered.';
          statline.innerHTML=
            stat('Basal energy (BMR)',fmt(bmr)+' <small>kcal</small>')+
            stat('Activity factor','x '+stageB.f)+
            stat('Bird group',bt==='p'?'Passerine':'Non-passerine');
          noteBox.style.display='block';
          noteBox.innerHTML='<strong>Estimate:</strong> avian needs differ a lot between species. This is a starting point, not a species-specific ration. A formulated pelleted diet plus appropriate fresh food is the safest base.';
          feedNote.innerHTML=feedText('bird');feedNote.style.display='block';
        }
        else if(s.model==='horse'){
          var stageH=s.stages[+$('stage').value];
          var deKcal=stageH.c*kg; // kcal/day
          state.der=deKcal;
          heroLab.textContent='Daily digestible energy';
          heroVal.innerHTML=fmt(deKcal)+' <small>kcal/day</small>';
          heroRng.textContent=(deKcal/1000).toFixed(1)+' Mcal per day. Most of this should come from forage (hay or pasture).';
          statline.innerHTML=
            stat('Per day (Mcal)',(deKcal/1000).toFixed(1)+' <small>Mcal</small>')+
            stat('Energy rate',stageH.c+' <small>kcal/kg</small>')+
            stat('Body weight',fmt(kg)+' <small>kg</small>');
          feedNote.innerHTML=feedText('horse');feedNote.style.display='block';
          // horse portion default hint uses kcal/kg of forage
        }
        else if(s.model==='rumin'){
          var prod=$('stage').value;
          var dmiPct=s.dmi[prod];
          var dmiKg=kg*dmiPct/100;
          var meMcal=s.mecoef*Math.pow(kg,0.75)*(prod==='maint'?1:(prod==='grow'?1.5:2.2));
          state.der=null;
          heroLab.textContent='Estimated dry matter intake';
          heroVal.innerHTML=dmiKg.toFixed(1)+' <small>kg DM/day</small>';
          heroRng.textContent='About '+dmiPct+'% of body weight in dry matter. As-fed hay weighs more because of moisture.';
          statline.innerHTML=
            stat('Dry matter intake',dmiKg.toFixed(1)+' <small>kg/day</small>')+
            stat('Maintenance energy','~'+meMcal.toFixed(1)+' <small>Mcal ME</small>')+
            stat('As % body weight',dmiPct+'%');
          noteBox.style.display='block';
          noteBox.innerHTML='<strong>Important:</strong> ruminant feeding depends on production stage, pasture quality and diet energy density. This is a practical intake estimate only. For growth, pregnancy, lactation or feedlot targets, balance a proper ration with your vet or a livestock nutritionist.';
          feedNote.innerHTML=feedText('rumin');feedNote.style.display='block';
          $('portionCard').style.display='none'; // portion converter not meaningful for pasture ruminants
        }
        else if(s.model==='pig'){
          var stageP=s.stages[+$('stage').value];
          var meP=110*Math.pow(kg,0.75)*stageP.f; // kcal ME/day
          state.der=meP;
          heroLab.textContent='Daily energy target';
          heroVal.innerHTML=fmt(meP)+' <small>kcal ME/day</small>';
          heroRng.textContent='Pigs are usually fed a balanced grower or sow ration. Feed to maintain good, not fat, body condition.';
          statline.innerHTML=
            stat('Metabolic weight',Math.pow(kg,0.75).toFixed(1)+' <small>kg^0.75</small>')+
            stat('Stage factor','x '+stageP.f)+
            stat('Body weight',fmt(kg)+' <small>kg</small>');
          noteBox.style.display='block';
          noteBox.innerHTML='<strong>Estimate:</strong> commercial pig diets are formulated to stage. Use a complete grower, finisher or sow feed and follow the bag guide, adjusting to body condition.';
          feedNote.innerHTML=feedText('pig');feedNote.style.display='block';
        }
        res.style.display='block';
        res.scrollIntoView({behavior:'smooth',block:'start'});
      }

      function lastTarget(){
        // grab hero number for portion calc (kcal), only for kcal species
        var s=SPECIES[state.species];
        if(s.model==='rumin')return null;
        var v=$('heroVal').textContent.replace(/[^0-9.]/g,'');
        return parseFloat(v);
      }

      function stat(k,v){return '<div class="stat"><div class="k">'+k+'</div><div class="v">'+v+'</div></div>';}

      function feedText(sp){
        var t={
          dog:'<b>Split the day:</b> feed adult dogs across two meals. Treats and chews count. Keep treats under 10% of daily calories so the main diet stays balanced.',
          cat:'<b>Cats do better with small, frequent meals</b> or a measured amount left out. Watch water intake too. A steady weight and a waistline you can feel are the goals.',
          ferret:'<b>Ferrets have a fast gut</b> and eat little and often. High protein, high fat, low carbohydrate. Fresh food should always be available.',
          rabbit:'<b>Unlimited grass hay is the foundation</b> and is fed free choice on top of this figure. The calorie amount here mainly covers pellets and greens. Too many pellets cause obesity and gut problems.',
          guinea:'<b>Unlimited grass hay plus daily vitamin C</b> is essential. The calorie figure covers pellets and fresh veg. Guinea pigs cannot make their own vitamin C.',
          bird:'<b>Base the diet on a formulated pellet</b> with measured fresh vegetables and limited seed. Seed only diets cause obesity and deficiency. Weigh your bird on a gram scale each week.',
          horse:'<b>Forage first.</b> Aim for at least 1.5% of body weight per day in forage dry matter. Add hard feed only if forage cannot meet needs. Make all diet changes slowly over 7 to 14 days.',
          rumin:'<b>Forage and pasture do most of the work.</b> Match dry matter intake to pasture quality and top up with hay or concentrate as needed. Provide clean water and appropriate minerals.',
          pig:'<b>Use a complete stage-appropriate ration</b> and follow the feed guide. Avoid overfeeding, which causes lameness and reproductive problems in breeding pigs.'
        };
        return t[sp]||'';
      }

      function gramFmt(g){ return g>=1000 ? (g/1000).toFixed(2)+' <small>kg</small>' : Math.round(g)+' <small>g</small>'; }

      var PLABEL={can:'cans',pouch:'pouches',serving:'servings',scoop:'scoops'};

      function mealTiles(meals,grams,cups){
        var out=[];
        for(var i=0;i<meals;i++){
          if(cups!=null){
            out.push('<div class="meal-tile"><div class="mv">'+(cups/meals).toFixed(2)+'</div><div class="ml">cups</div></div>');
          } else {
            var per=grams/meals;
            var disp=per>=1000?(per/1000).toFixed(2):Math.round(per);
            var u=per>=1000?'kg':'grams';
            out.push('<div class="meal-tile"><div class="mv">'+disp+'</div><div class="ml">'+u+'</div></div>');
          }
        }
        return out.join('');
      }
      function mealTilesUnit(meals,total,unit){
        var out=[];
        for(var i=0;i<meals;i++){ out.push('<div class="meal-tile"><div class="mv">'+(total/meals).toFixed(2)+'</div><div class="ml">'+unit+'</div></div>'); }
        return out.join('');
      }

      function portion(){
        var portionErr=$('portionErr');
        portionErr.style.display='none';
        function portionError(message,fieldId){
          portionErr.textContent=message;
          portionErr.style.display='block';
          if(fieldId)root.querySelector(`#${fieldId}`)?.focus();
        }
        if(!state.der){portionError('Calculate the daily calorie target before converting it into food portions.');return;}
        var kcalPerG=null,kcalPerCup=null,kcalPerPack=null,packUnit='',isDry=false,lastsNote='';
        var der=state.der;

        if(state.foodMode==='preset'){
          var arr=PRESETS[FOODGROUP[state.species]]||[];
          var p=arr[+$('foodPreset').value];
          if(!p){portionError('Choose a food type before converting the calorie target.','foodPreset');return;}
          kcalPerG=p.k/100; isDry=!!p.dry;
        } else {
          var unit=$('foodUnit').value;
          if(unit==='peramount'){
            var av=parseFloat($('amtVal').value);
            var ak=parseFloat($('amtKcal').value);
            var au=$('amtUnit').value;
            if(!(av>0)||!isFinite(av)||av>1000000){portionError('Enter a valid food amount greater than zero and check the selected amount unit.','amtVal');return;}
            if(!(ak>0)||!isFinite(ak)||ak>10000000){portionError('Enter valid calories for that food amount and check the label value.','amtKcal');return;}
            if(au==='g'){ kcalPerG=ak/av; lastsNote='At this rate, '+(+av)+' g of that food lasts about '+(ak/der).toFixed(1)+' days.'; }
            else if(au==='kg'){ kcalPerG=ak/(av*1000); lastsNote='At this rate, '+(+av)+' kg of that food lasts about '+(ak/der).toFixed(1)+' days.'; }
            else if(au==='cup'){ kcalPerCup=ak/av; }
            else { kcalPerPack=ak/av; packUnit=au; }
          } else {
            var fk=parseFloat($('foodKcal').value);
            var maxFood=unit==='perkg'?20000:(unit==='per100g'?2000:5000);
            if(!(fk>0)||!isFinite(fk)||fk>maxFood){portionError('Enter a valid food energy value and confirm that its unit matches the label.','foodKcal');return;}
            if(unit==='per100g'){ kcalPerG=fk/100; isDry=true; }
            else if(unit==='perkg'){ kcalPerG=fk/1000; isDry=true; }
            else { kcalPerCup=fk; }
          }
        }

        var meals=+$('meals').value;
        var out=$('portionOut');
        var pDay=$('pDay');
        var pCup=$('pCup');
        var tiles=$('mealTiles');
        var caveat=$('portionCaveat');

        var name=$('foodName').value.trim();
        var pf=$('portionFor');
        if(name){ pf.style.display='block'; pf.textContent='Feeding guide for '+name; } else { pf.style.display='none'; }

        if(kcalPerPack){
          var lbl=PLABEL[packUnit]||'servings';
          var packs=der/kcalPerPack;
          $('pDayStat').style.display='';
          $('pCupStat').style.display='none';
          document.querySelector('#pDayStat .k').textContent='Feed per day';
          pDay.innerHTML=packs.toFixed(2)+' <small>'+lbl+'</small>';
          tiles.innerHTML=mealTilesUnit(meals,packs,lbl);
          caveat.textContent='Based on the calories in one '+packUnit+' that you entered. Round to whole '+lbl+' where it makes practical sense and adjust to body condition.';
        } else if(kcalPerCup){
          var cups=der/kcalPerCup;
          $('pDayStat').style.display='none';
          $('pCupStat').style.display='';
          pCup.innerHTML=cups.toFixed(2)+' <small>cups</small>';
          tiles.innerHTML=mealTiles(meals,null,cups);
          caveat.textContent='Your food only lists calories per cup, so the result is in cups. If the bag also shows kcal per kg, switch to that option and weigh the food for better accuracy.';
        } else {
          var showCups=$('showCups').checked;
          var grams=der/kcalPerG;
          $('pDayStat').style.display='';
          document.querySelector('#pDayStat .k').textContent='Feed per day';
          pDay.innerHTML=gramFmt(grams);
          if(isDry && showCups){
            $('pCupStat').style.display='';
            pCup.innerHTML='about '+(grams/120).toFixed(2)+' <small>cups*</small>';
            caveat.textContent='*Cups are a rough guide only. A standard measuring cup of dry food is assumed at about 120 g, but this varies by food. Weighing on a kitchen scale is far more accurate.';
          } else {
            $('pCupStat').style.display='none';
            caveat.textContent = lastsNote ? ('Weighing food on a kitchen scale is the most accurate way to feed. '+lastsNote) : 'Weigh food on a kitchen scale for the most accurate result. Cups vary a lot between foods.';
          }
          tiles.innerHTML=mealTiles(meals,grams,null);
        }
        $('portionCard').classList.add('has-portions');
        out.style.display='block';
        out.scrollIntoView({behavior:'smooth',block:'nearest'});
      }

      function resultText(){
        var species=SPECIES[state.species].nm;
        var lines=['ASK A VET™ Daily Calorie Calculator',species+' result: '+$('heroVal').textContent.trim()];
        var range=$('heroRng').textContent.trim();
        if(range)lines.push(range);
        root.querySelectorAll('#statline .stat').forEach(function(el){
          lines.push(el.querySelector('.k').textContent.trim()+': '+el.querySelector('.v').textContent.trim());
        });
        if($('portionOut').style.display==='block'){
          var day=$('pDayStat');
          var cup=$('pCupStat');
          if(day.style.display!=='none')lines.push('Food per day: '+$('pDay').textContent.trim());
          if(cup.style.display!=='none')lines.push('Food per day: '+$('pCup').textContent.trim());
        }
        lines.push('General estimate only. Monitor body condition and consult your veterinarian for an individual feeding plan.','https://askavet.com/pages/daily-calorie-calculator');
        return lines.join('\n');
      }

      function copyResults(btn){
        var text=resultText();
        if(navigator.clipboard&&window.isSecureContext){
          navigator.clipboard.writeText(text).then(function(){var old=btn.textContent;btn.textContent='Copied';setTimeout(function(){btn.textContent=old},1600)});
        }else{
          var ta=document.createElement('textarea');ta.value=text;document.body.appendChild(ta);ta.select();document.execCommand('copy');ta.remove();
          var old=btn.textContent;btn.textContent='Copied';setTimeout(function(){btn.textContent=old},1600);
        }
      }

      function emailResults(){
        var subject=encodeURIComponent('ASK A VET™ '+SPECIES[state.species].nm+' calorie result');
        window.location.href='mailto:?subject='+subject+'&body='+encodeURIComponent(resultText());
      }

      function startAgain(){
        $('weight').value='';$('target').value='';$('foodName').value='';
        $('foodUnit').value='per100g';$('foodKcal').value='';
        $('amtVal').value='';$('amtKcal').value='';$('amtUnit').value='g';
        $('meals').value='2';$('showCups').checked=false;
        setFoodMode('preset');onFoodUnitChange();
        $('result').style.display='none';$('portionOut').style.display='none';
        $('portionErr').style.display='none';$('portionCard').classList.remove('has-portions');
        state.der=null;selectSpecies('dog');window.scrollTo({top:0,behavior:'smooth'});setTimeout(function(){$('weight').focus()},400);
      }




      // Static controls
      $('modeOwner').addEventListener('click', function(){ setMode('owner'); });
      $('modeVet').addEventListener('click', function(){ setMode('vet'); });
      $('uKg').addEventListener('click', function(){ setUnit('kg'); });
      $('uLb').addEventListener('click', function(){ setUnit('lb'); });
      $('uG').addEventListener('click', function(){ setUnit('g'); });
      $('mPreset').addEventListener('click', function(){ setFoodMode('preset'); });
      $('mLabel').addEventListener('click', function(){ setFoodMode('label'); });
      $('foodUnit').addEventListener('change', onFoodUnitChange);
      $('calculateButton').addEventListener('click', calc);
      $('portionButton').addEventListener('click', portion);
      $('startAgainButton').addEventListener('click', startAgain);
      $('copyResultsButton').addEventListener('click', function(){ copyResults(this); });
      $('emailResultsButton').addEventListener('click', emailResults);
      $('printResultsButton').addEventListener('click', function(){ window.print(); });

      // init
      selectSpecies('dog');

    }
  }

  customElements.define(ELEMENT_NAME, CalorieCalculator);
})();
